import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaRocket, FaLightbulb, FaChartLine, FaUsers, FaCogs, FaAward, FaTrophy, FaArrowLeft, FaCheckCircle, FaBuilding, FaGlobe, FaHandshake, FaMapMarkerAlt, FaPhone, FaExternalLinkAlt, FaCalendarAlt, FaIndustry, FaGraduationCap, FaUserGraduate, FaEye, FaDownload, FaBookReader } from 'react-icons/fa';
import { Helmet } from 'react-helmet';
import AnimatedCounter from '../components/AnimatedCounter';
import CertificateNotification from '../components/CertificateNotification';
import { isPWA } from '../utils/pwaDetector';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function Home() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isInPWA, setIsInPWA] = useState(false);

  // بررسی اینکه آیا در PWA هستیم یا browser
  useEffect(() => {
    setIsInPWA(isPWA());
  }, []);

  const features = [
    {
      icon: <FaLightbulb className="text-5xl" />,
      title: 'ایده‌های نوآورانه',
      description: 'تبدیل ایده‌های خلاقانه به طرح‌های اجرایی',
      color: 'from-secondary-400 to-secondary-600'
    },
    {
      icon: <FaRocket className="text-5xl" />,
      title: 'شتاب‌دهی',
      description: 'توسعه سریع و حرفه‌ای کسب‌وکار',
      color: 'from-primary-400 to-primary-600'
    },
    {
      icon: <FaCogs className="text-5xl" />,
      title: 'پشتیبانی فنی',
      description: 'مشاوره و راهنمایی در مسیر توسعه',
      color: 'from-accent-400 to-accent-600'
    },
    {
      icon: <FaChartLine className="text-5xl" />,
      title: 'رشد پایدار',
      description: 'مسیری روشن به سوی موفقیت',
      color: 'from-accent-500 to-accent-700'
    },
    {
      icon: <FaUsers className="text-5xl" />,
      title: 'شبکه‌سازی',
      description: 'ارتباط با سرمایه‌گذاران و متخصصان',
      color: 'from-primary-500 to-primary-700'
    },
    {
      icon: <FaAward className="text-5xl" />,
      title: 'مجوزها',
      description: 'دریافت مجوز هسته فناور',
      color: 'from-secondary-500 to-secondary-700'
    }
  ];

  useEffect(() => {
    // Track page view
    const trackPageView = async () => {
      try {
        await axios.post(`${API_URL}/api/stats/page-view`);
      } catch (error) {
        console.error('Error tracking page view:', error);
      }
    };
    trackPageView();

    // Load statistics
    const loadStats = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/stats`);
        setStats(response.data);
      } catch (error) {
        console.error('Error loading stats:', error);
        // Set default values on error
        setStats({
          total_registrations: 0,
          unique_users: 0,
          event_participation: 250,
          total_page_views: 0,
          total_app_downloads: 0,
          education_stats: {}
        });
      } finally {
        setLoading(false);
      }
    };
    loadStats();
  }, []);

  return (
    <div className="min-h-screen transition-colors duration-300">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>نوآفرین‌شو | سامانه ثبت طرح‌های فناور دانشگاه شمال</title>
        <meta name="description" content="سامانه رسمی ثبت و مدیریت طرح‌های فناور و هسته‌های نوآور مرکز رشد و کارآفرینی بین‌المللی دانشگاه شمال. جشنواره امید، ثبت ایده، شتابدهی استارتاپ" />
        <meta name="keywords" content="نوآفرین شو، دانشگاه شمال، مرکز رشد، کارآفرینی، هسته فناور، ثبت طرح، جشنواره امید، استارتاپ، شتابدهی، TRL، دانش بنیان، نوآوری، فناوری" />
        <meta property="og:title" content="نوآفرین‌شو | سامانه ثبت طرح‌های فناور" />
        <meta property="og:description" content="پلتفرم رسمی مرکز رشد و کارآفرینی دانشگاه شمال برای ثبت و مدیریت طرح‌های نوآورانه" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Hero Section - طراحی حرفه‌ای، شیک و بدون لگ */}
      <section className="relative min-h-[calc(100vh-5rem)] lg:min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Background - بهینه شده برای موبایل */}
        <div className="absolute inset-0">
          {/* Base Professional Gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-blue-50 dark:from-gray-900 dark:via-slate-900 dark:to-blue-950"></div>
          
          {/* Subtle Animated Orbs - CSS Only for Performance */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="hero-orb hero-orb-1"></div>
            <div className="hero-orb hero-orb-2"></div>
            <div className="hero-orb hero-orb-3"></div>
          </div>

          {/* Professional Grid Pattern */}
          <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.08]"
               style={{
                 backgroundImage: `linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)`,
                 backgroundSize: '40px 40px'
               }}>
          </div>

          {/* Premium Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/40 to-white/80 dark:from-transparent dark:via-gray-900/40 dark:to-gray-900/80"></div>
        </div>

        {/* Content Container */}
        <div className="relative container mx-auto px-4 sm:px-6 z-10 py-16 sm:py-20">
          <div className="text-center max-w-6xl mx-auto">
            {/* Logo - Elegant & Simple */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="mb-10 sm:mb-12 relative inline-block"
            >
              <div className="relative">
                {/* Logo Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-primary-500/20 to-secondary-500/20 rounded-3xl blur-3xl scale-110"></div>
                
                {/* Logo Container - Clean Design */}
                <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-2xl rounded-3xl p-6 sm:p-8 shadow-2xl border border-gray-200/50 dark:border-gray-700/50">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_500dd289-4b7e-4ffb-9065-26d7bb62264f/artifacts/9xom66md_Logo%20NOAFARIN%20%20SHO.jpg"
                    alt="لوگو نوآفرین‌شو"
                    className="h-24 w-24 sm:h-28 sm:w-28 md:h-32 md:w-32 object-contain"
                  />
                  
                  {/* Elegant Corner Accents */}
                  <div className="absolute top-2 left-2 w-6 h-6 border-t-3 border-l-3 border-primary-500/30 rounded-tl-xl"></div>
                  <div className="absolute top-2 right-2 w-6 h-6 border-t-3 border-r-3 border-secondary-500/30 rounded-tr-xl"></div>
                  <div className="absolute bottom-2 left-2 w-6 h-6 border-b-3 border-l-3 border-secondary-500/30 rounded-bl-xl"></div>
                  <div className="absolute bottom-2 right-2 w-6 h-6 border-b-3 border-r-3 border-primary-500/30 rounded-br-xl"></div>
                </div>
              </div>
            </motion.div>

            {/* Main Title - Professional Typography */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-10 sm:mb-12"
            >
              <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-extrabold mb-6 sm:mb-8 leading-tight hero-logo-font">
                <span className="inline-block text-gray-900 dark:text-white tracking-tight">
                  نوآفرین
                </span>
                <span className="inline-block text-transparent bg-clip-text bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 dark:from-amber-400 dark:via-yellow-400 dark:to-amber-500 font-black tracking-tight">
                  شو
                </span>
              </h1>
              
              {/* Elegant Divider */}
              <div className="flex items-center justify-center gap-3 mb-6 sm:mb-8">
                <div className="h-px w-16 sm:w-24 bg-gradient-to-r from-transparent to-primary-300"></div>
                <div className="w-2 h-2 rounded-full bg-primary-500 hero-pulse"></div>
                <div className="h-px w-16 sm:w-24 bg-gradient-to-l from-transparent to-primary-300"></div>
              </div>

              {/* Subtitle - Professional Card */}
              <div className="inline-block">
                <div className="relative px-6 sm:px-10 py-3 sm:py-4 bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-xl">
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 dark:text-gray-100">
                    سامانه ثبت طرح‌های فناور
                  </p>
                </div>
              </div>

              {/* University Name */}
              <p className="mt-5 sm:mt-6 text-base sm:text-lg md:text-xl font-semibold text-gray-600 dark:text-gray-400">
                مرکز رشد و کارآفرینی بین‌المللی دانشگاه شمال
              </p>
              <p className="mt-2 text-base sm:text-lg md:text-xl font-semibold text-gray-600 dark:text-gray-400">
                پارک علم و فناوری مازندران
              </p>
            </motion.div>

            {/* Stats - Premium Design */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6 max-w-5xl mx-auto mb-10 sm:mb-12"
            >
              {!loading && stats && (
                <>
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.5 }}
                    className="relative group cursor-default"
                  >
                    <div className="relative h-full bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl p-4 sm:p-5 md:p-6 shadow-lg border border-gray-200/50 dark:border-gray-700/50 hover:shadow-xl hover:border-primary-200 dark:hover:border-primary-800 transition-all duration-300">
                      <div className="text-3xl sm:text-4xl md:text-5xl font-extrabold bg-gradient-to-br from-primary-600 to-secondary-600 dark:from-primary-400 dark:to-secondary-400 bg-clip-text text-transparent mb-2">
                        <AnimatedCounter end={stats.total_registrations} suffix="+" />
                      </div>
                      <div className="text-gray-700 dark:text-gray-300 text-xs sm:text-sm md:text-base font-semibold">
                        طرح ثبت شده
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.6 }}
                    className="relative group cursor-default"
                  >
                    <div className="relative h-full bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl p-4 sm:p-5 md:p-6 shadow-lg border border-gray-200/50 dark:border-gray-700/50 hover:shadow-xl hover:border-primary-200 dark:hover:border-primary-800 transition-all duration-300">
                      <div className="text-3xl sm:text-4xl md:text-5xl font-extrabold bg-gradient-to-br from-primary-600 to-secondary-600 dark:from-primary-400 dark:to-secondary-400 bg-clip-text text-transparent mb-2">
                        <AnimatedCounter end={stats.unique_users} suffix="+" />
                      </div>
                      <div className="text-gray-700 dark:text-gray-300 text-xs sm:text-sm md:text-base font-semibold">
                        هسته فناور
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.7 }}
                    className="relative group cursor-default"
                  >
                    <div className="relative h-full bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl p-4 sm:p-5 md:p-6 shadow-lg border border-gray-200/50 dark:border-gray-700/50 hover:shadow-xl hover:border-primary-200 dark:hover:border-primary-800 transition-all duration-300">
                      <div className="text-3xl sm:text-4xl md:text-5xl font-extrabold bg-gradient-to-br from-primary-600 to-secondary-600 dark:from-primary-400 dark:to-secondary-400 bg-clip-text text-transparent mb-2">
                        <AnimatedCounter end={250} suffix="+" />
                      </div>
                      <div className="text-gray-700 dark:text-gray-300 text-xs sm:text-sm md:text-base font-semibold">
                        مشارکت در رویداد
                      </div>
                    </div>
                  </motion.div>
                </>
              )}
            </motion.div>

            {/* CTA Buttons - Professional Design */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 md:gap-5 justify-center items-stretch sm:items-center max-w-2xl mx-auto"
            >
              <button
                onClick={() => {
                  const festivalSection = document.getElementById('festivals-section');
                  festivalSection?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="group relative px-8 sm:px-10 md:px-12 py-4 sm:py-5 bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 hover:from-amber-600 hover:via-yellow-600 hover:to-amber-700 rounded-xl sm:rounded-2xl font-bold text-base sm:text-lg md:text-xl text-gray-900 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden"
                data-testid="view-festivals-btn"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                
                <span className="relative z-10 flex items-center justify-center gap-2 sm:gap-3">
                  <FaTrophy className="text-xl sm:text-2xl" />
                  <span>مشاهده جشنواره‌ها</span>
                </span>
              </button>

              <button
                onClick={() => {
                  const featuresSection = document.getElementById('features-section');
                  featuresSection?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="px-8 sm:px-10 md:px-12 py-4 sm:py-5 bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl border-2 border-gray-300 dark:border-gray-600 hover:border-primary-400 dark:hover:border-primary-500 rounded-xl sm:rounded-2xl font-bold text-base sm:text-lg md:text-xl text-gray-800 dark:text-gray-100 hover:bg-white dark:hover:bg-gray-800 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <span className="flex items-center justify-center gap-2 sm:gap-3">
                  <FaLightbulb className="text-xl sm:text-2xl" />
                  <span>بیشتر بدانید</span>
                </span>
              </button>
            </motion.div>

            {/* Elegant Scroll Indicator - زیر دکمه‌ها */}
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1 }}
              className="mt-8 sm:mt-12 hero-scroll-indicator"
            >
              <div className="w-8 h-12 sm:w-10 sm:h-14 border-2 border-primary-400/60 dark:border-primary-500/60 rounded-full p-2 bg-white/20 dark:bg-gray-800/20 backdrop-blur-sm mx-auto">
                <div className="hero-scroll-dot"></div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* CSS Animations */}
        <style>{`
          @keyframes float-gentle {
            0%, 100% { transform: translate(0, 0) scale(1); }
            33% { transform: translate(30px, -30px) scale(1.1); }
            66% { transform: translate(-30px, 30px) scale(0.9); }
          }
          
          @keyframes pulse-soft {
            0%, 100% { opacity: 0.6; transform: scale(1); }
            50% { opacity: 1; transform: scale(1.2); }
          }
          
          @keyframes scroll-bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(8px); }
          }
          
          .hero-orb {
            position: absolute;
            border-radius: 50%;
            filter: blur(60px);
            opacity: 0.15;
          }
          
          .hero-orb-1 {
            top: 10%;
            right: 10%;
            width: 400px;
            height: 400px;
            background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
            animation: float-gentle 20s ease-in-out infinite;
          }
          
          .hero-orb-2 {
            bottom: 10%;
            left: 10%;
            width: 500px;
            height: 500px;
            background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
            animation: float-gentle 25s ease-in-out infinite 5s;
          }
          
          .hero-orb-3 {
            top: 50%;
            left: 50%;
            width: 300px;
            height: 300px;
            background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
            animation: float-gentle 30s ease-in-out infinite 10s;
            transform: translate(-50%, -50%);
          }
          
          .hero-pulse {
            animation: pulse-soft 2s ease-in-out infinite;
          }
          
          .hero-scroll-indicator {
            animation: scroll-bounce 2s ease-in-out infinite;
          }
          
          .hero-scroll-dot {
            width: 4px;
            height: 4px;
            background: currentColor;
            border-radius: 50%;
            margin: 0 auto;
            animation: scroll-bounce 1.5s ease-in-out infinite;
          }
          
          @media (prefers-reduced-motion: reduce) {
            .hero-orb,
            .hero-pulse,
            .hero-scroll-indicator,
            .hero-scroll-dot {
              animation: none;
            }
          }
        `}</style>
      </section>

      {/* Educational Statistics Section - بعد از Hero و قبل از جشنواره‌ها - Enhanced for PWA */}
      {!loading && stats && (
        <section id="education-stats-section" className="py-16 md:py-20 relative bg-gradient-to-br from-white via-blue-50 to-purple-50 dark:from-gray-800 dark:via-gray-800 dark:to-gray-900 transition-colors duration-300 overflow-hidden" data-testid="education-stats-section">
          {/* Decorative Background */}
          <div className="absolute inset-0 opacity-30 dark:opacity-20">
            <div className="absolute top-0 left-1/4 w-64 h-64 bg-primary-300 rounded-full filter blur-3xl animate-pulse"></div>
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-secondary-300 rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-12 md:mb-16"
            >
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 text-gradient-blue dark:bg-gradient-to-r dark:from-blue-400 dark:to-cyan-300 dark:bg-clip-text dark:text-transparent section-title">
                جامعه آماری نوآفرین‌شو
              </h2>
              <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                آمار مشارکت‌کنندگان بر اساس وضعیت تحصیلی
              </p>
            </motion.div>

            {/* Education Stats - دانش‌آموز و دانشجو جدا شده */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 max-w-5xl mx-auto mb-12">
              {(() => {
                // Define custom order and display names
                const educationConfig = [
                  { 
                    key: 'دانشجو_دانش‌آموز', 
                    label: 'دانشجو و دانش‌آموز',
                    icon: <FaBookReader className="text-3xl md:text-4xl text-white" />,
                    gradient: 'from-indigo-500 to-purple-600',
                    order: 1
                  },
                  { 
                    key: 'کارشناسی ارشد', 
                    label: 'کارشناسی ارشد',
                    icon: <FaUserGraduate className="text-3xl md:text-4xl text-white" />,
                    gradient: 'from-purple-600 to-pink-600',
                    order: 2
                  },
                  { 
                    key: 'دکتری', 
                    label: 'دکتری',
                    icon: <FaGraduationCap className="text-3xl md:text-4xl text-white" />,
                    gradient: 'from-amber-600 to-orange-600',
                    order: 3
                  },
                  { 
                    key: 'سایر', 
                    label: 'سایر',
                    icon: <FaUsers className="text-3xl md:text-4xl text-white" />,
                    gradient: 'from-gray-600 to-slate-700',
                    order: 4
                  }
                ];

                // Process education stats - combine کارشناسی، دانشجو، دانش‌آموز together
                const processedStats = {};
                let otherCount = 0;
                
                Object.entries(stats.education_stats).forEach(([level, count]) => {
                  // کارشناسی، دانشجو و دانش‌آموز همه با هم ترکیب می‌شوند
                  if (level === 'دانش‌آموز' || level === 'دانشجو' || level === 'کارشناسی') {
                    processedStats['دانشجو_دانش‌آموز'] = (processedStats['دانشجو_دانش‌آموز'] || 0) + count;
                  } else if (level === 'کارشناسی ارشد') {
                    processedStats['کارشناسی ارشد'] = (processedStats['کارشناسی ارشد'] || 0) + count;
                  } else if (level === 'دکتری') {
                    processedStats['دکتری'] = (processedStats['دکتری'] || 0) + count;
                  } else {
                    otherCount += count;
                  }
                });
                
                if (otherCount > 0) {
                  processedStats['سایر'] = otherCount;
                }

                // Filter and sort config based on available stats
                const displayItems = educationConfig
                  .filter(item => processedStats[item.key] !== undefined && processedStats[item.key] > 0)
                  .sort((a, b) => a.order - b.order);

                return displayItems.map((item, index) => (
                  <motion.div
                    key={item.key}
                    initial={{ opacity: 0, scale: 0.8, y: 20 }}
                    whileInView={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ scale: 1.05, y: -8 }}
                    className="bg-white dark:bg-gray-800 rounded-2xl md:rounded-3xl p-6 md:p-8 shadow-xl hover:shadow-2xl border-2 border-gray-200 dark:border-gray-700 hover:border-primary-400 dark:hover:border-primary-600 transition-all duration-300 cursor-pointer"
                  >
                    <div className="flex flex-col items-center text-center">
                      <motion.div 
                        whileHover={{ rotate: 360 }}
                        transition={{ duration: 0.6 }}
                        className={`w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br ${item.gradient} rounded-2xl flex items-center justify-center mb-4 shadow-lg`}
                      >
                        {item.icon}
                      </motion.div>
                      <div className={`text-4xl md:text-5xl lg:text-6xl font-extrabold bg-gradient-to-br ${item.gradient} bg-clip-text text-transparent mb-3`}>
                        <AnimatedCounter end={processedStats[item.key]} />
                      </div>
                      <div className="text-gray-700 dark:text-gray-300 text-sm md:text-base lg:text-lg font-bold">
                        {item.label}
                      </div>
                    </div>
                  </motion.div>
                ));
              })()}
            </div>

            {/* Platform Stats - Enhanced for PWA Mobile */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 max-w-5xl mx-auto"
            >
              {/* Page Views Card - Enhanced */}
              <motion.div
                whileHover={{ scale: 1.03, rotateY: 5 }}
                transition={{ duration: 0.3 }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-accent-500 to-accent-700 rounded-2xl md:rounded-3xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity"></div>
                <div className="relative bg-gradient-to-br from-accent-600 via-accent-700 to-accent-800 dark:from-accent-700 dark:via-accent-800 dark:to-accent-900 rounded-2xl md:rounded-3xl p-6 md:p-8 text-white shadow-2xl overflow-hidden">
                  {/* Animated Background Pattern */}
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 right-0 w-40 h-40 bg-white rounded-full -translate-y-1/2 translate-x-1/2"></div>
                    <div className="absolute bottom-0 left-0 w-32 h-32 bg-white rounded-full translate-y-1/2 -translate-x-1/2"></div>
                  </div>
                  
                  <div className="relative z-10">
                    <div className="flex items-center gap-4 mb-4">
                      <motion.div 
                        whileHover={{ scale: 1.2, rotate: 360 }}
                        transition={{ duration: 0.5 }}
                        className="w-14 h-14 md:w-16 md:h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg"
                      >
                        <FaEye className="text-2xl md:text-3xl" />
                      </motion.div>
                      <div>
                        <div className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-1">
                          <AnimatedCounter end={stats.total_page_views} suffix="+" />
                        </div>
                        <div className="text-white/90 text-base md:text-lg font-bold">بازدید سایت</div>
                      </div>
                    </div>
                    <p className="text-white/80 text-xs md:text-sm leading-relaxed">
                      تعداد کل بازدیدکنندگان سامانه نوآفرین‌شو
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* App Downloads Card - Enhanced */}
              <motion.div
                whileHover={{ scale: 1.03, rotateY: -5 }}
                transition={{ duration: 0.3 }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-secondary-500 to-secondary-700 rounded-2xl md:rounded-3xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity"></div>
                <div className="relative bg-gradient-to-br from-secondary-600 via-amber-600 to-amber-700 dark:from-secondary-700 dark:via-amber-700 dark:to-amber-800 rounded-2xl md:rounded-3xl p-6 md:p-8 text-gray-900 dark:text-white shadow-2xl overflow-hidden">
                  {/* Animated Background Pattern */}
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 left-0 w-40 h-40 bg-white rounded-full -translate-y-1/2 -translate-x-1/2"></div>
                    <div className="absolute bottom-0 right-0 w-32 h-32 bg-white rounded-full translate-y-1/2 translate-x-1/2"></div>
                  </div>
                  
                  <div className="relative z-10">
                    <div className="flex items-center gap-4 mb-4">
                      <motion.div 
                        whileHover={{ scale: 1.2, rotate: -360 }}
                        transition={{ duration: 0.5 }}
                        className="w-14 h-14 md:w-16 md:h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg"
                      >
                        <FaDownload className="text-2xl md:text-3xl" />
                      </motion.div>
                      <div>
                        <div className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-1">
                          <AnimatedCounter end={stats.total_app_downloads} suffix="+" />
                        </div>
                        <div className="text-gray-900 dark:text-white/90 text-base md:text-lg font-bold">دانلود اپلیکیشن</div>
                      </div>
                    </div>
                    <p className="text-gray-900/80 dark:text-white/80 text-xs md:text-sm leading-relaxed">
                      تعداد دانلود و نصب اپلیکیشن موبایل (PWA)
                    </p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Festivals Section - Active Festival: SHOMAL AI Connect 2025 */}
      <section id="festivals-section" className="py-16 md:py-20 relative bg-white dark:bg-gray-800 transition-colors duration-300" data-testid="festivals-section">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12 md:mb-16"
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 text-gradient-blue dark:bg-gradient-to-r dark:from-blue-400 dark:to-cyan-300 dark:bg-clip-text dark:text-transparent section-title">
              جشنواره‌های نوآفرین‌شو
            </h2>
            <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              فرصتی برای تبدیل ایده‌های نوآورانه به هسته‌های فناور موفق
            </p>
          </motion.div>

          {/* Festival Card - SHOMAL AI Connect 2025 - ACTIVE */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-6xl mx-auto"
          >
            <div className="bg-gradient-to-br from-slate-900 to-blue-900 rounded-3xl shadow-2xl overflow-hidden border-2 border-cyan-500/50 hover:border-cyan-400 transition-all duration-300">
              <div className="grid md:grid-cols-2 gap-6 p-6 md:p-8">
                {/* Poster */}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="relative overflow-hidden rounded-2xl shadow-xl"
                >
                  <img 
                    src="https://customer-assets.emergentagent.com/job_906637cf-e765-4c1b-8807-5e812e951cd7/artifacts/e6tvuvgm_2025-12-06%20%20%20Shomal%20Ai%20Connect%20Poster%20V05_page-0001.jpg"
                    alt="پوستر SHOMAL AI Connect 2025"
                    className="w-full h-auto object-contain max-h-[500px] mx-auto"
                  />
                  <div className="absolute top-4 right-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-3 py-2 rounded-xl font-bold shadow-lg text-sm animate-pulse">
                    <FaTrophy className="inline ml-2" />
                    ثبت‌نام فعال
                  </div>
                </motion.div>

                {/* Details */}
                <div className="flex flex-col justify-center">
                  <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent mb-4 flex items-center gap-3">
                    <FaTrophy className="text-cyan-400 text-2xl md:text-3xl" />
                    SHOMAL AI Connect 2025
                  </h3>
                  
                  <div className="mb-4 p-3 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-xl border border-cyan-500/50">
                    <p className="text-cyan-100 text-sm md:text-base font-semibold">
                      همایش تخصصی هوش مصنوعی - دوشنبه، ۶ دی ۱۴۰۴
                    </p>
                  </div>
                  
                  <p className="text-sm md:text-base lg:text-lg text-gray-300 mb-5 leading-relaxed">
                    ارتباط، نوآوری و آینده‌سازی با هوش مصنوعی. 
                    این همایش فرصتی ویژه برای آشنایی با آخرین دستاوردها در حوزه AI و 
                    شبکه‌سازی با متخصصان این حوزه است.
                  </p>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-start gap-2">
                      <FaCheckCircle className="text-cyan-400 text-lg mt-0.5 flex-shrink-0" />
                      <span className="text-sm md:text-base text-gray-300">سخنرانی تخصصی درباره آینده هوش مصنوعی</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <FaCheckCircle className="text-cyan-400 text-lg mt-0.5 flex-shrink-0" />
                      <span className="text-sm md:text-base text-gray-300">معرفی کاربرد AI در صنعت و دانشگاه</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <FaCheckCircle className="text-cyan-400 text-lg mt-0.5 flex-shrink-0" />
                      <span className="text-sm md:text-base text-gray-300">پنل گفت‌وگو با متخصصان</span>
                    </div>
                  </div>

                  <motion.button
                    onClick={() => navigate('/shomal-ai')}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-600 hover:from-cyan-600 hover:via-blue-600 hover:to-purple-700 text-white rounded-2xl font-bold text-base md:text-lg shadow-xl hover:shadow-2xl hover:shadow-cyan-500/50 transition-all"
                    data-testid="festival-details-btn"
                  >
                    ثبت‌نام در همایش
                    <FaArrowLeft className="text-lg" />
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features-section" className="py-16 md:py-20 relative bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 transition-colors duration-300" data-testid="features-section">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12 md:mb-16"
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 text-gradient-blue dark:bg-gradient-to-r dark:from-blue-400 dark:to-cyan-300 dark:bg-clip-text dark:text-transparent section-title">
              ویژگی‌های نوآفرین‌شو
            </h2>
            <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              ما به شما کمک می‌کنیم تا ایده‌هایتان را به واقعیت تبدیل کنید
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05, y: -10 }}
                className="group relative bg-white dark:bg-gray-800 rounded-3xl p-6 md:p-8 border-2 border-gray-200 dark:border-gray-700 hover:border-primary-400 dark:hover:border-primary-500 transition-all duration-300 shadow-lg hover:shadow-2xl"
                data-testid={`feature-card-${index}`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-5 rounded-3xl transition-opacity duration-300`}></div>
                <div className="relative z-10">
                  <div className={`inline-block p-4 bg-gradient-to-br ${feature.color} rounded-2xl mb-4 text-white shadow-lg`}>
                    {feature.icon}
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section - Redesigned */}
      <section className="py-16 md:py-24 relative bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 transition-colors duration-300 overflow-hidden" data-testid="about-section">
        {/* Decorative Background Elements */}
        <div className="absolute inset-0 opacity-5 dark:opacity-10">
          <div className="absolute top-10 right-10 w-72 h-72 bg-primary-400 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-secondary-400 rounded-full filter blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-7xl mx-auto"
          >
            {/* Header */}
            <div className="text-center mb-12 md:mb-16">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="inline-block"
              >
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-primary-800 dark:text-primary-300 flex items-center justify-center gap-4">
                  <FaBuilding className="text-secondary-500" />
                  درباره مرکز رشد
                </h2>
                <div className="h-1.5 bg-gradient-to-r from-transparent via-primary-500 to-transparent rounded-full"></div>
              </motion.div>
            </div>

            {/* Main Content Card */}
            <div className="grid md:grid-cols-12 gap-8 items-center">
              {/* Image Side */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7 }}
                viewport={{ once: true }}
                className="md:col-span-5"
              >
                <div className="relative group">
                  {/* Main Image Container */}
                  <div className="relative overflow-hidden rounded-3xl shadow-2xl">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_f58cc58a-4c8f-4cdf-9753-40be14e84470/artifacts/ic02bp7n_images__3_-removebg-preview%20%281%29.png"
                      alt="مرکز رشد و کارآفرینی دانشگاه شمال"
                      className="w-full h-auto object-cover transform group-hover:scale-105 transition-transform duration-700"
                    />
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-primary-900/30 via-transparent to-transparent"></div>
                  </div>
                  
                  {/* Decorative Elements */}
                  <div className="absolute -top-4 -right-4 w-24 h-24 bg-secondary-500 rounded-2xl opacity-20 transform rotate-12"></div>
                  <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-primary-500 rounded-2xl opacity-20 transform -rotate-12"></div>
                </div>
              </motion.div>

              {/* Text Side */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                viewport={{ once: true }}
                className="md:col-span-7"
              >
                <div className="space-y-6">
                  {/* Description Box */}
                  <div className="bg-gradient-to-br from-white via-blue-50/50 to-primary-50/50 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 backdrop-blur-lg rounded-2xl p-6 md:p-8 shadow-xl border border-primary-200/50 dark:border-primary-700/50">
                    <p className="text-gray-700 dark:text-gray-300 text-base md:text-lg leading-relaxed text-justify mb-6">
                      این سامانه، پلتفرم رسمی ثبت و مدیریت طرح‌های فناور مرکز رشد و کارآفرینی بین‌المللی دانشگاه شمال است. 
                      با هدف ایجاد زیرساخت‌های مورد نیاز کسب و کارهای نوآورانه، این سامانه فرصتی برای تبدیل ایده‌های خلاقانه 
                      به هسته‌های فناور موفق فراهم می‌کند.
                    </p>

                    {/* Feature Highlights */}
                    <div className="grid sm:grid-cols-2 gap-4 mb-6">
                      <div className="flex items-start gap-3 bg-white/80 dark:bg-gray-800/80 p-4 rounded-xl">
                        <FaLightbulb className="text-2xl text-secondary-500 flex-shrink-0 mt-1" />
                        <div>
                          <h4 className="font-bold text-gray-800 dark:text-gray-200 mb-1">نوآوری</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">حمایت از ایده‌های خلاقانه</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3 bg-white/80 dark:bg-gray-800/80 p-4 rounded-xl">
                        <FaHandshake className="text-2xl text-accent-500 flex-shrink-0 mt-1" />
                        <div>
                          <h4 className="font-bold text-gray-800 dark:text-gray-200 mb-1">همکاری</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">ارتباط با صنعت و دانشگاه</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3 bg-white/80 dark:bg-gray-800/80 p-4 rounded-xl">
                        <FaRocket className="text-2xl text-primary-500 flex-shrink-0 mt-1" />
                        <div>
                          <h4 className="font-bold text-gray-800 dark:text-gray-200 mb-1">شتاب‌دهی</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">رشد سریع کسب‌وکار</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3 bg-white/80 dark:bg-gray-800/80 p-4 rounded-xl">
                        <FaGlobe className="text-2xl text-primary-600 flex-shrink-0 mt-1" />
                        <div>
                          <h4 className="font-bold text-gray-800 dark:text-gray-200 mb-1">بین‌المللی</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">دسترسی به بازارهای جهانی</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Call to Action Box */}
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className="bg-gradient-to-r from-primary-600 to-primary-700 dark:from-primary-700 dark:to-primary-800 rounded-2xl p-6 shadow-2xl"
                  >
                    <div className="flex items-center gap-4 mb-3">
                      <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                        <FaGlobe className="text-2xl text-white" />
                      </div>
                      <h3 className="text-xl md:text-2xl font-bold text-white">
                        اطلاعات بیشتر
                      </h3>
                    </div>
                    <p className="text-white/90 mb-4">
                      برای اطلاعات کامل درباره مرکز رشد، به وب‌سایت اصلی مرکز مراجعه کنید
                    </p>
                    <a 
                      href="https://karafarini.shomal.ac.ir" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-white text-primary-700 px-6 py-3 rounded-xl font-bold hover:bg-secondary-500 hover:text-gray-900 transition-all duration-300 shadow-lg"
                    >
                      <FaArrowLeft className="text-lg" />
                      وب‌سایت مرکز رشد دانشگاه شمال
                    </a>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Growth Centers Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-gray-50 via-blue-50 to-primary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 relative overflow-hidden">
        {/* Background Decorations */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary-400/10 dark:bg-primary-600/10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-secondary-400/10 dark:bg-secondary-600/10 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="max-w-7xl mx-auto"
          >
            {/* Section Header */}
            <div className="text-center mb-16">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-primary-800 dark:text-primary-300 flex items-center justify-center gap-4">
                  <FaBuilding className="text-secondary-500" />
                  مراکز رشد و پارک فناوری
                </h2>
                <div className="h-1.5 bg-gradient-to-r from-transparent via-primary-500 to-transparent rounded-full"></div>
              </motion.div>
            </div>

            {/* Centers Grid - دو باکس در کنار هم با تناسب مناسب */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
              {/* Amol Technology Center */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-white via-blue-50/50 to-primary-50/50 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 rounded-2xl md:rounded-3xl shadow-2xl overflow-hidden border border-primary-200/50 dark:border-primary-700/50 hover:shadow-3xl transition-all duration-500 flex flex-col"
              >
                <div className="bg-gradient-to-r from-secondary-600 to-secondary-700 dark:from-secondary-700 dark:to-secondary-800 p-4 md:p-6">
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white flex items-center gap-2 md:gap-3">
                    <FaBuilding className="text-2xl md:text-3xl flex-shrink-0" />
                    <span>مرکز رشد واحدهای فناور آمل</span>
                  </h3>
                </div>
                
                <div className="p-4 sm:p-6 md:p-8 flex-grow">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                    <div className="flex items-start gap-3 bg-white/60 dark:bg-gray-800/60 rounded-xl p-3 md:p-4">
                      <FaCalendarAlt className="text-lg md:text-xl text-secondary-600 dark:text-secondary-400 flex-shrink-0 mt-1" />
                      <div className="min-w-0">
                        <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mb-1">تاریخ تاسیس</p>
                        <p className="text-sm md:text-base text-gray-700 dark:text-gray-300 font-semibold">1404/07/18</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 bg-white/60 dark:bg-gray-800/60 rounded-xl p-3 md:p-4">
                      <FaPhone className="text-lg md:text-xl text-secondary-600 dark:text-secondary-400 flex-shrink-0 mt-1" />
                      <div className="min-w-0">
                        <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mb-1">شماره مرکز</p>
                        <p className="text-sm md:text-base text-gray-700 dark:text-gray-300 font-semibold direction-ltr text-right">011-44442094</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-start gap-3 bg-white/60 dark:bg-gray-800/60 rounded-xl p-3 md:p-4">
                      <FaMapMarkerAlt className="text-lg md:text-xl text-secondary-600 dark:text-secondary-400 flex-shrink-0 mt-1" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mb-1">آدرس</p>
                        <p className="text-sm md:text-base text-gray-700 dark:text-gray-300 leading-relaxed">
                          مازندران، آمل، خیابان امام رضا، جنب مسجد امام رضا، ساختمان مرکز رشد و نوآوری شهرستان آمل
                        </p>
                        <p className="text-gray-600 dark:text-gray-400 text-xs md:text-sm mt-2 direction-ltr text-right">کد پستی: 4614916099</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 bg-white/60 dark:bg-gray-800/60 rounded-xl p-3 md:p-4">
                      <FaGlobe className="text-lg md:text-xl text-secondary-600 dark:text-secondary-400 flex-shrink-0 mt-1" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mb-1">وب‌سایت</p>
                        <a 
                          href="http://amol.mstp.ir/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm md:text-base text-secondary-600 dark:text-secondary-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors inline-flex items-center gap-2 break-all"
                        >
                          amol.mstp.ir
                          <FaExternalLinkAlt className="text-xs flex-shrink-0" />
                        </a>
                      </div>
                    </div>

                    <div className="bg-secondary-50 dark:bg-gray-900/50 rounded-xl p-3 md:p-4">
                      <div className="flex items-start gap-2 mb-3">
                        <FaIndustry className="text-lg md:text-xl text-secondary-600 dark:text-secondary-400 flex-shrink-0 mt-0.5" />
                        <p className="text-sm md:text-base text-gray-700 dark:text-gray-300 font-semibold">زمینه‌های فعالیت:</p>
                      </div>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <FaCheckCircle className="text-sm md:text-base text-secondary-500 flex-shrink-0 mt-0.5" />
                          <span className="text-xs md:text-sm text-gray-600 dark:text-gray-400">صنعت با اولویت ریخته‌گری و قطعه‌سازی</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <FaCheckCircle className="text-sm md:text-base text-secondary-500 flex-shrink-0 mt-0.5" />
                          <span className="text-xs md:text-sm text-gray-600 dark:text-gray-400">صنایع غذایی و کشاورزی با اولویت برنج و گیاهان داروئی</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <FaCheckCircle className="text-sm md:text-base text-secondary-500 flex-shrink-0 mt-0.5" />
                          <span className="text-xs md:text-sm text-gray-600 dark:text-gray-400">سایر حوزه‌های مورد نیاز منطقه</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Science and Technology Park - باکس دوم با تناسب مناسب */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-white via-purple-50/50 to-accent-50/50 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 rounded-2xl md:rounded-3xl shadow-2xl overflow-hidden border border-accent-200/50 dark:border-accent-700/50 hover:shadow-3xl transition-all duration-500 flex flex-col"
              >
                <div className="bg-gradient-to-r from-accent-600 to-accent-700 dark:from-accent-700 dark:to-accent-800 p-4 md:p-6">
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white flex items-center gap-2 md:gap-3">
                    <FaGlobe className="text-2xl md:text-3xl flex-shrink-0" />
                    <span>پارک علم و فناوری مازندران</span>
                  </h3>
                </div>
                
                <div className="p-4 sm:p-6 md:p-8 flex-grow flex flex-col justify-between">
                  <div className="space-y-4 mb-6">
                    <div className="flex items-start gap-3 bg-white/60 dark:bg-gray-800/60 rounded-xl p-3 md:p-4">
                      <FaGlobe className="text-lg md:text-xl text-accent-600 dark:text-accent-400 flex-shrink-0 mt-1" />
                      <div className="flex-1">
                        <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mb-1">وب‌سایت</p>
                        <a 
                          href="https://www.mstp.ir" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm md:text-base text-accent-600 dark:text-accent-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors flex items-center gap-2 font-semibold"
                        >
                          www.mstp.ir
                          <FaExternalLinkAlt className="text-xs flex-shrink-0" />
                        </a>
                      </div>
                    </div>

                    <div className="bg-accent-50 dark:bg-gray-900/50 rounded-xl p-3 md:p-4">
                      <p className="text-sm md:text-base text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                        پارک علم و فناوری مازندران به عنوان یکی از زیرساخت‌های مهم علمی و فناوری استان، نقش مهمی در توسعه و رشد شرکت‌های دانش‌بنیان و فناور ایفا می‌کند.
                      </p>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="flex items-start gap-2">
                          <FaCheckCircle className="text-accent-500 flex-shrink-0 mt-0.5" />
                          <span className="text-xs md:text-sm text-gray-600 dark:text-gray-400">حمایت از شرکت‌های دانش‌بنیان</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <FaCheckCircle className="text-accent-500 flex-shrink-0 mt-0.5" />
                          <span className="text-xs md:text-sm text-gray-600 dark:text-gray-400">توسعه فناوری‌های نوین</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <FaCheckCircle className="text-accent-500 flex-shrink-0 mt-0.5" />
                          <span className="text-xs md:text-sm text-gray-600 dark:text-gray-400">ارتباط صنعت و دانشگاه</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <FaCheckCircle className="text-accent-500 flex-shrink-0 mt-0.5" />
                          <span className="text-xs md:text-sm text-gray-600 dark:text-gray-400">توسعه اکوسیستم نوآوری</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-accent-100 to-accent-200 dark:from-accent-900/30 dark:to-accent-800/30 rounded-xl md:rounded-2xl p-4 md:p-6 text-center">
                    <FaBuilding className="text-4xl md:text-5xl text-accent-600 dark:text-accent-400 mx-auto mb-3" />
                    <p className="text-base md:text-lg text-gray-800 dark:text-gray-200 font-bold mb-2">
                      رویکرد اکوسیستم نوآوری
                    </p>
                    <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">
                      ایجاد ارتباط میان صنعت، دانشگاه و دولت
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>

          </motion.div>
        </div>
      </section>

      {/* Novayar Notification - فقط در Browser نمایش داده شود، نه PWA */}
      {!isInPWA && <CertificateNotification />}
    </div>
  );
}

export default Home;
